import { computed } from 'vue'
import type { UseAppTextOptions, UseAppTextReturn } from '../types/interfaces'
import type { ResponsiveProp } from '../types/types'
import { APP_TEXT_TAG_MAP } from '../config/app-text.config'

function resolveResponsiveProp<T extends string> (
  prop: ResponsiveProp<T> | undefined,
  defaultValue: T,
): Record<string, T> {
  if (prop === undefined) {
    return { base: defaultValue }
  }

  if (typeof prop === 'string') {
    return { base: prop }
  }

  return prop as Record<string, T>
}

function generateClassNames (
  resolved: Record<string, string>,
  prefix: string,
): string[] {
  const classes: string[] = []

  for (const [bp, value] of Object.entries(resolved)) {
    if (bp === 'base') {
      classes.push(`app-text--${prefix}${value}`)
    }
    else {
      classes.push(`app-text--${bp}-${prefix}${value}`)
    }
  }

  return classes
}

export function useAppText (options: UseAppTextOptions): UseAppTextReturn {
  const { props, onClick } = options

  const resolvedTag = computed<string>(() => {
    if (props.tag) {
      return props.tag
    }

    if (props.href) {
      return 'a'
    }

    const resolvedName = resolveResponsiveProp(props.name, 'body2')
    const baseName = resolvedName.base

    if (baseName && baseName in APP_TEXT_TAG_MAP) {
      return APP_TEXT_TAG_MAP[baseName as keyof typeof APP_TEXT_TAG_MAP]
    }

    return 'p'
  })

  const classList = computed<string[]>(() => {
    const classes: string[] = ['app-text']

    const resolvedName = resolveResponsiveProp(props.name, 'body2')
    classes.push(...generateClassNames(resolvedName, ''))

    const baseName = resolvedName.base || 'body2'
    const defaultColor = baseName.startsWith('h') ? 'slate-900' : 'slate-700'
    const resolvedColor = resolveResponsiveProp(props.color, defaultColor)
    classes.push(...generateClassNames(resolvedColor, 'color-'))

    const resolvedAlign = resolveResponsiveProp(props.align, 'inherit')
    classes.push(...generateClassNames(resolvedAlign, 'align-'))

    const resolvedDecoration = resolveResponsiveProp(props.textDecoration, 'none')
    classes.push(...generateClassNames(resolvedDecoration, 'decoration-'))

    if (props.block) classes.push('app-text--block')
    if (props.truncate) classes.push('app-text--truncate')

    return classes
  })

  const linkAttrs = computed<Record<string, string> | null>(() => {
    if (!props.href) {
      return null
    }

    const attrs: Record<string, string> = { href: props.href }

    if (props.target) {
      attrs.target = props.target

      if (props.target === '_blank') {
        attrs.rel = 'noopener noreferrer'
      }
    }

    return attrs
  })

  function handleClick (): void {
    onClick?.()
  }

  return {
    resolvedTag,
    classList,
    linkAttrs,
    handleClick,
  }
}
